import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Loader2,
  Home,
  Plus,
  AlertCircle,
  Sparkles,
  Receipt,
  Truck,
  ShieldCheck,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getStyleById } from "@/constants/artStyles";
import { formatPrice } from "@/lib/utils";
import { formatSizeDisplay, getValueLabel } from "@/lib/orderDisplayUtils";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";
import { PrivacySafeShareCard } from "@/components/features/artwork/PrivacySafeShareCard";

interface OrderData {
  id: string;
  format_size: string;
  price_cents: number;
  selected_style: string;
  generated_image_url: string | null;
  support_material: string | null;
  finish_type: string | null;
  frame_option: string | null;
  frame_color: string | null;
  quality_tier: string | null;
  shipping_address: Record<string, string> | null;
  shipping_cost_cents: number | null;
  customer_name: string | null;
  customer_email: string | null;
  special_instructions: string | null;
  status: string;
  created_at: string;
}

type PageState = "loading" | "success" | "already_processed" | "error";

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [order, setOrder] = useState<OrderData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [printWarning, setPrintWarning] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    if (!sessionId) {
      setPageState("error");
      setErrorMessage("Session de paiement introuvable.");
      return;
    }

    const processPayment = async () => {
      try {
        trackFunnelEvent("payment_confirmation_started");
        const { data, error } = await supabase.functions.invoke(
          "process-artwork-payment",
          { body: { sessionId } }
        );

        if (error) throw error;

        if (data?.order) {
          setOrder(data.order);
        }

        if (data?.alreadyProcessed) {
          setPageState("already_processed");
        } else {
          setPageState("success");
        }

        if (data?.warning) {
          setPrintWarning(true);
        }

        trackFunnelEvent("order_confirmed", {
          alreadyProcessed: !!data?.alreadyProcessed,
          status: data?.order?.status,
          style: data?.order?.selected_style,
          formatSize: data?.order?.format_size,
          priceCents: data?.order?.price_cents,
          hasPrintWarning: !!data?.warning,
        });
      } catch (err) {
        console.error("Payment processing failed:", err);
        trackFunnelEvent("order_confirmation_failed", {
          errorType: err instanceof Error ? err.name : "unknown",
        });
        setPageState("error");
        setErrorMessage(
          "Une erreur est survenue lors du traitement de votre paiement. Veuillez contacter le support."
        );
      }
    };

    processPayment();
  }, [searchParams]);

  const styleName =
    order?.selected_style
      ? getStyleById(order.selected_style)?.name || order.selected_style
      : null;

  const shippingAddress = order?.shipping_address as Record<string, string> | null;
  const customerName =
    [shippingAddress?.firstName, shippingAddress?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || order?.customer_name;
  const cityLine =
    [shippingAddress?.postCode, shippingAddress?.city]
      .filter(Boolean)
      .join(" ")
      .trim() || null;
  const framingLabel =
    order?.frame_option && order.frame_option !== "none"
      ? `${getValueLabel("frameOption", order.frame_option)}${
          order.frame_color ? ` - ${getValueLabel("frameColor", order.frame_color)}` : ""
        }`
      : null;

  const formatProductDetails = () => {
    const details: { label: string; value: string }[] = [];

    if (order?.format_size) {
      details.push({ label: "Taille", value: formatSizeDisplay(order.format_size) });
    }
    if (order?.support_material) {
      details.push({ label: "Matériau", value: getValueLabel("material", order.support_material) });
    }
    if (order?.finish_type) {
      details.push({ label: "Finition", value: getValueLabel("finish", order.finish_type) });
    }
    if (order?.frame_option && order.frame_option !== "none") {
      const frameLabel = getValueLabel("frameOption", order.frame_option);
      const colorLabel = order.frame_color
        ? ` - ${getValueLabel("frameColor", order.frame_color)}`
        : "";
      details.push({ label: "Encadrement", value: `${frameLabel}${colorLabel}` });
    } else {
      details.push({ label: "Encadrement", value: "Sans cadre" });
    }

    return details;
  };

  const shippingCents = order?.shipping_cost_cents || 0;
  const totalCents = (order?.price_cents || 0) + shippingCents;

  if (pageState === "loading") {
    return (
      <div className="min-h-screen pb-12 pt-28 md:pt-32 gradient-hero">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center py-20">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-6 text-primary" />
            <h1 className="text-2xl font-bold mb-2">
              Traitement de votre commande...
            </h1>
            <p className="text-foreground/70">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === "error") {
    return (
      <div className="min-h-screen pb-12 pt-28 md:pt-32 gradient-hero">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center py-20">
            <AlertCircle className="w-16 h-16 mx-auto mb-6 text-destructive" />
            <h1 className="text-2xl font-bold mb-2">
              Une erreur est survenue
            </h1>
            <p className="text-foreground/70 mb-8">{errorMessage}</p>
            <Button onClick={() => navigate("/")} size="lg">
              <Home className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 pt-28 md:pt-32 gradient-hero">
      <div className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              {pageState === "already_processed"
                ? "Commande déjà confirmée"
                : "Commande confirmée !"}
            </h1>
            <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
              {pageState === "already_processed"
                ? "Cette commande a déjà été prise en compte."
                : "Votre portrait part en préparation. Vous recevrez un email de suivi dès que l'expédition sera lancée."}
            </p>
          </div>

          {printWarning && (
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl text-sm text-yellow-700 dark:text-yellow-400">
              Votre paiement a bien été validé. Le traitement de l'impression
              peut prendre quelques minutes supplémentaires.
            </div>
          )}

          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(340px,0.8fr)]">
            {order?.generated_image_url && (
              <div className="relative self-start rounded-[28px] overflow-hidden border border-border/20 bg-background/70 shadow-xl">
                <img
                  src={order.generated_image_url}
                  alt="Votre oeuvre d'art"
                  className="block w-full max-h-[620px] object-cover bg-black/5 dark:bg-white/5"
                />
              </div>
            )}

            <div className="space-y-6">
              <Card className="gradient-card border-border/20 rounded-[28px]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5" />
                    Et maintenant ?
                  </CardTitle>
                </CardHeader>
                  <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                        1
                      </div>
                      <div>
                        <p className="font-medium">Verification de votre commande</p>
                        <p className="text-sm text-muted-foreground">
                          Nous validons le fichier et les parametres d'impression.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                        2
                      </div>
                      <div>
                        <p className="font-medium">Production de votre portrait</p>
                        <p className="text-sm text-muted-foreground">
                          L'impression est lancee dans notre atelier partenaire.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                        3
                      </div>
                      <div>
                        <p className="font-medium">Expedition avec suivi</p>
                        <p className="text-sm text-muted-foreground">
                          Comptez environ <span className="font-medium text-foreground">7 a 14 jours ouvres</span>.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="gradient-card border-border/20 rounded-[28px]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Receipt className="w-5 h-5" />
                    Resume de commande
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {order?.id && (
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-muted-foreground">Reference</span>
                        <span className="font-mono font-medium">
                          {order.id.slice(0, 8).toUpperCase()}
                        </span>
                      </div>
                    )}
                    {styleName && (
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-muted-foreground">Style</span>
                        <span className="font-medium">{styleName}</span>
                      </div>
                    )}
                    {order?.format_size && (
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-muted-foreground">Format</span>
                        <span className="font-medium">
                          {formatSizeDisplay(order.format_size)}
                        </span>
                      </div>
                    )}
                    {framingLabel && (
                      <div className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-muted-foreground">Encadrement</span>
                        <span className="font-medium">
                          {framingLabel}
                        </span>
                      </div>
                    )}
                  </div>

                  {(customerName || cityLine || shippingAddress?.country) && (
                    <div className="rounded-2xl bg-background/70 p-4 text-sm">
                      <div className="flex items-center gap-2 font-medium mb-1">
                        <Truck className="w-4 h-4" />
                        Livraison
                      </div>
                      <p className="text-muted-foreground">
                        {customerName ? `Pour ${customerName}` : "Adresse confirmee"}
                        {cityLine ? `, ${cityLine}` : ""}
                        {shippingAddress?.country ? `, ${shippingAddress.country}` : ""}
                      </p>
                    </div>
                  )}

                  <div className="border-t border-border/20 pt-4">
                    <div className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">Total paye</span>
                      <span className="text-lg font-semibold text-foreground">
                        {order ? formatPrice(totalCents) : "—"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="gradient-card border-primary/15 rounded-[28px] bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ShieldCheck className="w-5 h-5" />
                    Informations utiles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-6 text-muted-foreground">
                  <p>
                    Conservez l'email de confirmation : il récapitule votre commande et sert de référence si vous contactez le support.
                  </p>
                  <p>
                    Vous recevrez le suivi d'expédition dès que l'atelier partenaire aura remis le colis au transporteur.
                  </p>
                  <p>
                    Si une information de livraison semble incorrecte, contactez-nous rapidement avec votre référence de commande.
                  </p>
                </CardContent>
              </Card>

              {order?.selected_style && (
                <PrivacySafeShareCard selectedStyle={order.selected_style} />
              )}

              <div className="flex flex-col gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  size="lg"
                  className="w-full border-border/30 bg-background/70 hover:bg-background hover:text-foreground"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Retour a l'accueil
                </Button>
                <Button
                  onClick={() => navigate("/create")}
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Faire une nouvelle oeuvre
                </Button>
              </div>
            </div>
          </div>

          {order?.customer_email && (
            <p className="text-center text-sm text-muted-foreground mt-8">
              Un recapitulatif a ete envoye a <span className="font-medium text-foreground">{order.customer_email}</span>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
